import { Container, Graphics, Text } from 'pixi.js'

export interface EntityState {
  id: string
  x: number
  y: number
  hp: number
  maxHp: number
  isPlayer: boolean
}

let entityIdCounter = 0

export abstract class Entity {
  id: string
  x: number
  y: number
  hp: number
  maxHp: number
  isPlayer: boolean
  isSelected: boolean = false
  isDead: boolean = false

  container: Container
  protected healthBar: Graphics
  protected selectionRing: Graphics

  constructor(x: number, y: number, hp: number, isPlayer: boolean) {
    this.id = `entity_${entityIdCounter++}`
    this.x = x
    this.y = y
    this.hp = hp
    this.maxHp = hp
    this.isPlayer = isPlayer

    this.container = new Container()
    this.container.position.set(x, y)
    this.container.sortableChildren = true

    // Selection ring (rendered below unit)
    this.selectionRing = new Graphics()
    this.selectionRing.zIndex = -1
    this.selectionRing.visible = false
    this.container.addChild(this.selectionRing)

    // Health bar (rendered above unit)
    this.healthBar = new Graphics()
    this.healthBar.zIndex = 100
    this.container.addChild(this.healthBar)
  }

  abstract update(dt: number): void
  abstract render(): void

  setPosition(x: number, y: number): void {
    this.x = x
    this.y = y
    this.container.position.set(x, y)
    // For y-sorting
    this.container.zIndex = y
  }

  takeDamage(amount: number): void {
    this.hp = Math.max(0, this.hp - amount)
    if (this.hp <= 0) {
      this.isDead = true
    }
    this.updateHealthBar()
  }

  heal(amount: number): void {
    this.hp = Math.min(this.maxHp, this.hp + amount)
    this.updateHealthBar()
  }

  setSelected(selected: boolean): void {
    this.isSelected = selected
    this.selectionRing.visible = selected
  }

  protected updateHealthBar(): void {
    this.healthBar.clear()

    const barWidth = 32
    const barHeight = 4
    const x = -barWidth / 2
    const y = -24

    // Background
    this.healthBar.rect(x, y, barWidth, barHeight)
    this.healthBar.fill({ color: 0x333333 })

    // Health
    const hpPercent = this.hp / this.maxHp
    const healthColor = hpPercent > 0.5 ? 0x2ecc71 :
                       hpPercent > 0.25 ? 0xf39c12 : 0xe74c3c

    this.healthBar.rect(x, y, barWidth * hpPercent, barHeight)
    this.healthBar.fill({ color: healthColor })
  }

  protected drawSelectionRing(radius: number, color: number): void {
    this.selectionRing.clear()
    this.selectionRing.circle(0, 0, radius)
    this.selectionRing.stroke({ width: 2, color })
  }

  getState(): EntityState {
    return {
      id: this.id,
      x: this.x,
      y: this.y,
      hp: this.hp,
      maxHp: this.maxHp,
      isPlayer: this.isPlayer
    }
  }

  // Distance to another entity
  distanceTo(other: Entity): number {
    const dx = other.x - this.x
    const dy = other.y - this.y
    return Math.sqrt(dx * dx + dy * dy)
  }

  // Distance to a point
  distanceToPoint(x: number, y: number): number {
    const dx = x - this.x
    const dy = y - this.y
    return Math.sqrt(dx * dx + dy * dy)
  }

  // Check if point is within a radius
  isInRange(x: number, y: number, range: number): boolean {
    return this.distanceToPoint(x, y) <= range
  }

  destroy(): void {
    this.container.destroy({ children: true })
  }
}
